import { useSuspenseQuery } from '@apollo/client';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { DetailsList } from '@eluve/blocks';
import {
  Card,
  CardContent,
  CardHeader,
  ColDefBuilder,
  DataTable,
  Stack,
  Text,
  useToast,
} from '@eluve/components';
import { formatToLocale } from '@eluve/date-utils';
import { latestHumanSOAPNoteFragment } from '@eluve/frontend-appointment-hooks';
import { graphql } from '@eluve/graphql.tada';
import { formatHumanName } from '@eluve/utils';

import { SlaFailureReasonSection } from './components/SlaFailureReasonSection';

type SummaryFeedbackRow = {
  type: string | null;
  comment: string | null;
  tags: string[];
  createdAt: string;
  rating: number | null;
  llmSummaryId?: string;
  promptTemplateName?: string;
};

const feedbackColumns = new ColDefBuilder<SummaryFeedbackRow>()
  .defaultSortable('type', 'Type')
  .defaultSortable('rating', 'Rating')
  .defaultSortable('comment', 'Comment')
  .colDef({
    accessorKey: 'tags',
    header: () => {
      return <div>Tags</div>;
    },
    cell: ({ row }) => {
      return <div>{row.original.tags.join(', ')}</div>;
    },
  })
  .defaultSortable('llmSummaryId', 'LLM Summary ID')
  .defaultSortable('promptTemplateName', 'Prompt Template')
  .defaultSortable('createdAt', 'Created At')
  .build();

// prettier-ignore
const getAdminTenantAppointmentSummary = graphql(
  `#graphql
    query getAdminTenantAppointmentSummary($tenantId: uuid!, $appointmentId: uuid!) {
      appointmentsByPk(tenantId: $tenantId, id: $appointmentId) {
        __typename
        id
        name
        startDate
        endDate
        createdAt
        hasFailedSla
        patient {
          id
        }
        user {
          id
          __typename
          firstName
          lastName
          email
        }
        segments {
          __typename
          id
          device {
            __typename
            microphoneName
            deviceType
            manufacturer
            model
            browserName
            operatingSystem
          }
        }
        feedback(orderBy: { createdAt: DESC }) {
          __typename
          id
          type
          comment
          createdAt
          summaryFeedbackTags {
            feedbackTag {
              name
            }
          }
          rating
          llmOutputId
        }
        doctor_interaction {
          __typename
          appointmentId
          noteSignedAt
          additionalNotes
          externalChartUrl
          externalEhrSyncStatus
        }
        external_charts {
          __typename
          id
          externalChartId
          chartUrl
        }
        appointment_llm_outputs {
          __typename
          llmOutputId
          output {
            __typename
            id
            modelPromptTemplate {
              __typename
              id
              promptTemplateId
              template {
                __typename
                id
                name
              }
            }
          }
        }
        slaFailureReasonTags: tags(where: { tag: { tag: { _eq: "HumanApplied.SLA_FAILURE_REASON" } } }) {
          __typename
          id
          metadata
          createdAt
          tag {
            __typename
            id
            tag
          }
        }
      }
    }
  `,
  [latestHumanSOAPNoteFragment],
);

export const AdminAppointment: React.FC = () => {
  const { tenantId, appointmentId } = useParams() as {
    tenantId: string;
    appointmentId: string;
  };
  const toast = useToast();

  const {
    data: { appointmentsByPk: appointment },
    error,
  } = useSuspenseQuery(getAdminTenantAppointmentSummary, {
    variables: {
      tenantId,
      appointmentId,
    },
  });

  useEffect(
    function showToastOnError() {
      if (error) {
        toast.error('Failed to load appointment details');
      }
    },
    [error, toast],
  );

  const user = appointment?.user;

  const noteSignedAt = appointment?.doctor_interaction?.noteSignedAt;
  const externalChartUrl = appointment?.external_charts?.[0]?.chartUrl;
  const externalEhrSyncStatus =
    appointment?.doctor_interaction?.externalEhrSyncStatus;

  // Collect device information from appointment segments
  const devices =
    appointment?.segments?.flatMap((s) => (s.device ? [s.device] : [])) ?? [];

  return (
    <div>
      <SlaFailureReasonSection
        tenantId={tenantId}
        appointmentId={appointmentId}
        hasFailedSla={appointment?.hasFailedSla ?? null}
        existingTag={appointment?.slaFailureReasonTags?.[0] ?? null}
      />

      <DetailsList
        title={{ label: 'Info' }}
        items={[
          { label: 'Name', value: appointment?.name ?? '' },
          { label: 'User Id', value: user?.id },
          {
            label: 'User Name',
            value: formatHumanName(user?.firstName, user?.lastName),
          },
          { label: 'Email', value: user?.email },
          {
            label: 'Start Date',
            value: formatToLocale(appointment?.startDate),
          },
          {
            label: 'End Date',
            value: formatToLocale(appointment?.endDate) || undefined,
          },
          {
            label: 'Created At',
            value: formatToLocale(appointment?.createdAt),
          },
        ]}
      />

      {devices.length === 0 ? (
        <DetailsList
          title={{ label: 'Hardware Information' }}
          items={[
            { label: 'Devices', value: 'No device information available' },
          ]}
        />
      ) : (
        devices.map((device, index) => (
          <DetailsList
            key={`${device.browserName}-${device.operatingSystem}-${device.microphoneName}-${index}`}
            title={{
              label:
                devices.length > 1
                  ? `Hardware Information - Device ${index + 1}`
                  : 'Hardware Information',
            }}
            items={[
              {
                label: 'Microphone',
                value: device.microphoneName ?? undefined,
              },
              { label: 'Browser', value: device.browserName ?? undefined },
              {
                label: 'Operating System',
                value: device.operatingSystem ?? undefined,
              },
              { label: 'Device Type', value: device.deviceType ?? undefined },
              {
                label: 'Device Manufacturer',
                value: device.manufacturer ?? undefined,
              },
              { label: 'Device Model', value: device.model ?? undefined },
            ]}
          />
        ))
      )}

      <DetailsList
        title={{ label: 'Sync Status' }}
        items={[
          {
            label: 'Note Signed At',
            value: formatToLocale(noteSignedAt),
          },
          {
            label: 'External Chart URL',
            value: externalChartUrl ?? undefined,
          },
          { label: 'EHR Sync Status', value: externalEhrSyncStatus },
        ]}
      />

      <Card className="mt-5 w-full rounded-xl border-border-primary">
        <CardHeader className="flex h-10 flex-row place-items-center justify-between space-y-0 rounded-t-xl border-b bg-bg-primary p-0 px-5 tracking-normal">
          <div>
            <Text size="md">Feedback Details</Text>
          </div>
        </CardHeader>
        <CardContent>
          <Stack className="m-5" gap={2} align="stretch">
            <Text size="md">
              Number of feedbacks: {appointment?.feedback?.length ?? 0}
            </Text>
          </Stack>
          <div className="m-4">
            {Boolean(appointment?.feedback?.length) && (
              <DataTable
                data={
                  appointment?.feedback?.map((sf) => {
                    const matchingOutput =
                      appointment?.appointment_llm_outputs?.find(
                        (llmSummary) =>
                          llmSummary.llmOutputId === sf.llmOutputId,
                      )?.output;

                    return {
                      comment: sf.comment,
                      type: sf.type,
                      createdAt: formatToLocale(sf.createdAt),
                      tags: (sf.summaryFeedbackTags || []).map(
                        (sft) => sft.feedbackTag.name,
                      ),
                      rating: sf.rating,
                      llmSummaryId: matchingOutput?.id,
                      promptTemplateName:
                        matchingOutput?.modelPromptTemplate?.template?.name,
                    };
                  }) ?? []
                }
                columns={feedbackColumns}
                enableGlobalSearch
              />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
